import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { canManageStaff, decodeRoles, hasCockpitAccess } from '../../lib/auth'
import { canSeeDispatch } from '../../lib/dispatch-api'
import { canSeeKitchen } from '../../lib/kitchen-api'
import { t, type Lang } from '../../lib/i18n'
import { KITCHEN_LABEL } from '../kitchen/kitchen-i18n'
import { st } from '../staff/staff-i18n'
import { dt } from '../dispatch/dispatch-i18n'
import { sx } from '../settings/settings-i18n'
import { canSeeSettings } from '../../lib/settings-api'
import { CommandHeader } from '../../components/CommandHeader'
import { ActivityFeed } from './ActivityFeed'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

export function CockpitScreen({ lang, onLangToggle, isOnline }: Props) {
  const [authState, setAuthState] = useState<'checking' | 'ok' | 'redirect' | 'kitchen'>('checking')

  useEffect(() => {
    hasCockpitAccess().then((ok) => {
      if (ok) return setAuthState('ok')
      // R199 (b) — a kitchen login has no cockpit role; it lands on its own screen, not /login.
      setAuthState(decodeRoles().includes('kitchen') ? 'kitchen' : 'redirect')
    })
  }, [])

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  // ux1 (i): the session check shows a bar with a spinner, never a blank screen.
  if (authState === 'checking') {
    return (
      <div dir={dir} className="min-h-screen bg-background font-sans">
        <div
          role="status"
          aria-live="polite"
          data-auth-checking
          className="sticky top-0 z-10 h-16 px-4 flex items-center gap-2 bg-primary text-primary-foreground shadow"
        >
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          <span className="text-sm">{t(lang, 'checkingSession')}</span>
        </div>
      </div>
    )
  }
  if (authState === 'kitchen') return <Navigate to="/kitchen" replace />
  if (authState === 'redirect') return <Navigate to="/login" replace />

  // bench-CMS-UNDER-OPS-HOST.CASTLE §2⑥ — the CMS entry, owner/admin only.
  // decodeRoles() reads the session cache synchronously; it is populated by the
  // hasCockpitAccess() gate above, so by this line authState is already 'ok'.
  // Roles are enumerated INDIVIDUALLY, never 'not a driver': a role added later
  // must be granted this entry deliberately, not inherit it by failing a negative.
  const roles = decodeRoles()
  const canSeeCms = roles.includes('owner') || roles.includes('admin')
  // bench-STAFF-PASSWORD-RESET-SCREEN.CB-B §1.9 — owner/architect, via canManageStaff()
  // (does not consult isReadOnly, unlike most of this screen's own write gating).
  const canSeeStaff = canManageStaff()
  // R4 — Bilal steps in for Nour: hop/admin only.
  const canSeeDispatchLink = canSeeDispatch(roles)

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      <div className="sticky top-0 z-10 bg-primary text-primary-foreground shadow">
        <CommandHeader
          title={t(lang, 'appTitle')}
          lang={lang}
          onLangToggle={onLangToggle}
          isOnline={isOnline}
          showHome={false}
        />
        <nav className="px-4 pb-3 flex flex-wrap items-center gap-2">
          <Link
            to="/orders/new"
            className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
          >
            {t(lang, 'navNewOrder')}
          </Link>
          <Link
            to="/board"
            className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
          >
            {t(lang, 'navBoard')}
          </Link>
          {canSeeKitchen(roles) && (
            // R199 (b) — /kitchen (features/kitchen/KitchenScreen.tsx), beside Board.
            <Link
              to="/kitchen"
              className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
            >
              {(lang === 'ar' ? KITCHEN_LABEL.title.ar : null) ?? KITCHEN_LABEL.title.en}
            </Link>
          )}
          {canSeeCms && (
            // A PLAIN ANCHOR, DELIBERATELY — do not "fix" this into a react-router <Link>.
            // /cms is the ops-web Next app on the SAME host, OUTSIDE this SPA's router:
            // <Link to="/cms"> would be resolved against BrowserRouter's basename and match
            // no route here. Leaving the router is the point, so this needs a document load.
            <a
              href="/cms"
              className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
            >
              {t(lang, 'navCatalogOrders')}
            </a>
          )}
          {canSeeStaff && (
            // /staff IS inside this SPA's router (features/staff/StaffScreen.tsx) — a real
            // <Link>, unlike /cms above.
            <Link
              to="/staff"
              className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
            >
              {st(lang, 'title')}
            </Link>
          )}
          {canSeeSettings(roles) && (
            // R503 (d): /settings (features/settings/SettingsScreen.tsx), owner and architect only.
            <Link
              to="/settings"
              className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
            >
              {sx(lang, 'title')}
            </Link>
          )}
          {canSeeDispatchLink && (
            // R4 — Bilal steps in for Nour: /dispatch (features/dispatch/DispatchScreen.tsx).
            <Link
              to="/dispatch"
              title={dt(lang, 'title')}
              className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
            >
              {dt(lang, 'title')}
            </Link>
          )}
          {/* bench-STAFF-LOGIN-OFF-AND-OWN-PASSWORD.CB-B §1.9 — every signed-in user gets
              this link, no role gate (unlike CMS/Staff above): reaching CockpitScreen at all
              already means a valid, authenticated session (hasCockpitAccess() gate). */}
          <Link
            to="/account"
            className="inline-flex items-center justify-center bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground text-sm font-semibold px-3 py-1.5 rounded-md min-h-[44px] whitespace-nowrap shrink-0 transition-colors"
          >
            {st(lang, 'accountNavLink')}
          </Link>
        </nav>
      </div>

      <main className="px-4 py-6 max-w-2xl mx-auto pb-10">
        <ActivityFeed lang={lang} roles={roles} />
      </main>
    </div>
  )
}
